"use strict";
/**
 * Conversation Types for Link-Based AI Architecture
 *
 * This module defines the types for artifact-linked conversations,
 * replacing the previous mode-based (ASK/AGENT/PLAN/DIAGRAM) approach.
 *
 * Key concepts:
 * - Conversations are linked to specific artifacts (documents, SSAs, diagrams, plans)
 * - AI capabilities are derived from what artifacts are linked
 * - Each link creates context isolation for aerospace compliance
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.deriveCapabilities = deriveCapabilities;
exports.deriveEffectiveMode = deriveEffectiveMode;
exports.hasAnyEditCapability = hasAnyEditCapability;
exports.createPrimaryArtifactKey = createPrimaryArtifactKey;
exports.parsePrimaryArtifactKey = parsePrimaryArtifactKey;
/**
 * Derive AI capabilities from conversation links
 *
 * This replaces the old mode-based capability checks:
 * - Before: `if (aiMode === 'agent') { ... }`
 * - After: `if (capabilities.canEditDocuments) { ... }`
 *
 * @param links - Array of artifact links for the conversation
 * @returns Capabilities object indicating what the AI can do
 */
function deriveCapabilities(links, projectType) {
    return {
        canEditDocuments: links.some(l => l.artifactType === 'document'),
        canEditSSA: links.some(l => l.artifactType === 'ssa'),
        canEditDiagrams: links.some(l => l.artifactType === 'diagram'),
        canEditPlans: links.some(l => l.artifactType === 'plan'),
        canDevelop: links.some(l => l.artifactType === 'coding'),
        canSearch: true,
        canReadProject: true,
        canWriteCreative: projectType === 'creative',
        canWriteEngineering: projectType === 'aerospace' || projectType === 'technical',
    };
}
/**
 * Derive effective mode from links for UI display and backward compatibility
 *
 * @param links - Array of artifact links
 * @returns EffectiveMode for the conversation
 */
function deriveEffectiveMode(links) {
    if (links.length === 0) {
        return 'read-only';
    }
    const types = new Set(links.map(l => l.artifactType));
    if (types.size > 1) {
        return 'multi-edit';
    }
    const singleType = links[0].artifactType;
    switch (singleType) {
        case 'document': return 'document-edit';
        case 'ssa': return 'ssa-edit';
        case 'diagram': return 'diagram-edit';
        case 'plan': return 'plan-edit';
        case 'coding': return 'coding-develop';
        default: return 'read-only';
    }
}
/**
 * Check if capabilities allow any editing
 */
function hasAnyEditCapability(capabilities) {
    return (capabilities.canEditDocuments ||
        capabilities.canEditSSA ||
        capabilities.canEditDiagrams ||
        capabilities.canEditPlans ||
        capabilities.canDevelop);
}
/**
 * Create the primary artifact key for DynamoDB GSI
 * Format: "{artifactType}#{artifactId}" e.g., "document#DOC-123"
 *
 * @param links - Array of conversation links
 * @returns Primary artifact key string or undefined if no links
 */
function createPrimaryArtifactKey(links) {
    if (links.length === 0)
        return undefined;
    const primary = links[0];
    return `${primary.artifactType}#${primary.artifactId}`;
}
/**
 * Parse a primary artifact key back to type and ID
 *
 * @param key - Key in format "{type}#{id}"
 * @returns Parsed type and id, or undefined if invalid
 */
function parsePrimaryArtifactKey(key) {
    const parts = key.split('#');
    if (parts.length !== 2)
        return undefined;
    const [type, id] = parts;
    if (!['document', 'ssa', 'diagram', 'plan', 'coding'].includes(type))
        return undefined;
    return { artifactType: type, artifactId: id };
}
//# sourceMappingURL=conversation.js.map